import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { backendEndPointToken, terminologyCanonicalUrlBaseToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';
import { ActivityDefinition, Coding, CodeableConcept, Observation, Quantity, Range, Patient, Extension } from 'fhir/r4';
import { Observable, forkJoin, map, of, switchMap, throwError } from 'rxjs';
import { FormFieldsSelectDataService } from '../shared/form-fields-select-data.service';
import { LoadingUIEnabled } from '../loading.interceptor';

export interface ObservationReferenceRange {
  type?: CodeableConcept;
  low?: Quantity;
  high?: Quantity;
  appliesTo?: CodeableConcept[];
  age?: Range;
  text?: string;
}

export interface ObservationReferenceRangeTemplate {
  id?: string;
  code?: Coding;
  display?: string;
  range: ObservationReferenceRange;
  lastUpdated?: string;
}

export interface ReferenceRangeSuggestion extends ObservationReferenceRangeTemplate {
  // For normal workflow: 'low' | 'normal' | 'high' | 'unknown'
  // For typed workflow: implementation-defined token (e.g. 'critical', 'hypertension-stage-1')
  classification: string;
  workflow: 'normal' | 'typed';
}

@Injectable({ providedIn: 'root' })
export class ObservationReferenceRangeService {
  private readonly http = inject(HttpClient);
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly canonicalBase = inject(terminologyCanonicalUrlBaseToken);
  private readonly auth = inject(AuthService);
  private readonly formFieldsDataService = inject(FormFieldsSelectDataService);

  private adminGenderCodes: string[] = [];

  private readonly extensionUrl = `${this.canonicalBase}/StructureDefinition/observation-reference-range`;

  listTemplates(filter?: { code?: Coding }): Observable<ObservationReferenceRangeTemplate[]> {
    // Some FHIR servers do not support searching ActivityDefinition by `kind`.
    // Build a conservative query and perform strict client-side filtering below.
    const params: string[] = ['_count=200', '_sort=-_lastUpdated'];
    const orgId = this.auth.getOrganizationId();
    if (orgId) {
      params.push(`_tag=organization|${encodeURIComponent(orgId)}`);
    }
    if (filter?.code?.system && filter.code.code) {
      params.push(`code=${encodeURIComponent(`${filter.code.system}|${filter.code.code}`)}`);
    }
    const url = `${this.backendUrl}/ActivityDefinition?${params.join('&')}`;
    return this.http.get<any>(url, { context: new HttpContext().set(LoadingUIEnabled, false) }).pipe(
      map(bundle => (bundle?.entry ?? [])
        // Keep only entries that contain an ActivityDefinition resource
        .map((e: any) => e.resource)
        .filter((r: any) => r && r.resourceType === 'ActivityDefinition')
        // Ensure template extension is present and (optionally) code matches filter
        .filter((r: any) => {
          try {
            const ext = (r.extension || []).find((x: any) => x?.url === `${this.canonicalBase}/StructureDefinition/observation-reference-range`);
            if (!ext) return false;
            if (filter?.code?.system && filter.code.code) {
              const coding = r.code?.coding?.[0];
              return coding && coding.system === filter.code.system && coding.code === filter.code.code;
            }
            return true;
          } catch (e) {
            return false;
          }
        })
        .map((res: any) => res as ActivityDefinition)
      ),
      map((resources: ActivityDefinition[]) => resources.map(resource => this.toTemplate(resource)))
    );
  }

  saveTemplate(template: ObservationReferenceRangeTemplate): Observable<ActivityDefinition> {
    if (!template.code?.code || !template.code.system) {
      return throwError(() => new Error('Observation code is required.'));
    }
    const payload = this.toActivityDefinitionPayload(template);
    if (template.id) {
      return this.http.put<ActivityDefinition>(`${this.backendUrl}/ActivityDefinition/${template.id}`, payload);
    }
    return this.http.post<ActivityDefinition>(`${this.backendUrl}/ActivityDefinition`, payload);
  }

  deleteTemplate(id: string): Observable<void> {
    return this.http.delete<void>(`${this.backendUrl}/ActivityDefinition/${id}`);
  }

  applyRangeToObservation(observation: Observation, range: ObservationReferenceRange): Observable<Observation> {
    if (!observation?.id) {
      return throwError(() => new Error('Observation id is required to apply a reference range.'));
    }
    const updated: Observation = {
      ...observation,
      referenceRange: [JSON.parse(JSON.stringify(range))]
    };
    return this.http.put<Observation>(`${this.backendUrl}/Observation/${observation.id}`, updated);
  }

  matchRangesForObservation(observation: Observation, patient?: Patient): Observable<ReferenceRangeSuggestion[]> {
    const coding = observation?.code?.coding?.[0];
    if (!coding) {
      return of([]);
    }
    return this.listTemplates({ code: coding }).pipe(
      map(templates => {console.log(templates);
// $#$        /* debugger; */
        return templates.filter(t => {
        
       return this.matchesPatientContext(t.range, patient)
      })}),
      map((templates: ObservationReferenceRangeTemplate[]) => {
        // Typed-range workflow: explicit typed ranges that contain the observation value
        const typed = this.classifyTypedRanges(observation, templates);

        // Normal-range workflow: ranges that are explicit normal or have no type (implicit normal)
        const normal = this.classifyNormalRanges(observation, templates);

        // Return typed matches first (explicit classifications), then normal-range-derived suggestions.
      console.log([...typed, ...normal])
// $#$      /* debugger; */
        return [...typed, ...normal];
      })
    );
  }

  // Typed-range classification: return templates whose interval contains the observation value,
  // using range.type as the returned classification token.
  private classifyTypedRanges(observation: Observation, templates: ObservationReferenceRangeTemplate[]): ReferenceRangeSuggestion[] {
    const value = Number(observation?.valueQuantity?.value);
    if (Number.isNaN(value)) return [];
    const matches: ReferenceRangeSuggestion[] = [];
    for (const t of templates) {
      const r = t.range;
      if (!r) continue;
      const lowN = r.low?.value != null ? Number(r.low.value) : null;
      const highN = r.high?.value != null ? Number(r.high.value) : null;
      const contains = (lowN == null || value >= lowN) && (highN == null || value <= highN);
      if (contains) {
        const typeText = this.rangeTypeLabel(r) || 'typed';
        matches.push({ ...t, classification: typeText, workflow: 'typed' });
      }
    }
    return matches;
  }

  // Normal-range interpretation: consider ranges whose type is 'normal' or missing
  // and derive low/normal/high (inclusive boundaries)
  private classifyNormalRanges(observation: Observation, templates: ObservationReferenceRangeTemplate[]): ReferenceRangeSuggestion[] {
    const value = Number(observation?.valueQuantity?.value);
    if (Number.isNaN(value)) return [];
    const results: ReferenceRangeSuggestion[] = [];
    for (const t of templates) {
      const r = t.range;
      if (!r) continue;
      const typeLabel = this.rangeTypeLabel(r);
      if (typeLabel && typeLabel.toLowerCase() !== 'normal') continue; // not a normal range
      const lowN = r.low?.value != null ? Number(r.low.value) : null;
      const highN = r.high?.value != null ? Number(r.high.value) : null;
      let classification = 'unknown';
      if (lowN != null && value < lowN) classification = 'low';
      else if ((lowN == null && highN == null)) classification = 'unknown';
      else if ((lowN == null || value >= lowN) && (highN == null || value <= highN)) classification = 'normal';
      else if (highN != null && value > highN) classification = 'high';
      results.push({ ...t, classification, workflow: 'normal' });
    }
    return results;
  }

  // Helper to derive a textual label for range.type (prefer code, then display, then text)
  private rangeTypeLabel(range?: ObservationReferenceRange): string | null {
    if (!range?.type) return null;
    const c = range.type.coding?.[0];
    if (c?.code) return String(c.code);
    if (c?.display) return String(c.display);
    if (range.type.text) return String(range.type.text);
    return null;
  }

  private toTemplate(resource: ActivityDefinition): ObservationReferenceRangeTemplate {
    const code = resource?.code?.coding?.[0];
    const extension = (resource?.extension ?? []).find(ext => ext.url === this.extensionUrl);
    const range = this.parseRangeExtension(extension);
    return {
      id: resource.id,
      code,
      display: resource.code?.text || code?.display || code?.code,
      range,
      lastUpdated: resource.meta?.lastUpdated
    };
  }

  private toActivityDefinitionPayload(template: ObservationReferenceRangeTemplate): ActivityDefinition {
    const coding: Coding[] = template.code ? [{ ...template.code }] : [];
    const extension = this.buildRangeExtension(template.range);
    return {
      resourceType: 'ActivityDefinition',
      id: template.id,
      status: 'active',
      experimental: false,
      kind: 'Observation',
      name: this.buildName(template),
      title: this.buildName(template),
      description: template.range?.text || 'Reference range template',
      code: coding.length
        ? {
          coding,
          text: template.display || coding[0].display || coding[0].code
        }
        : undefined,
      extension: extension ? [extension] : [],
      useContext: this.buildUseContext(template.range)
    } as ActivityDefinition;
  }

  private buildName(template: ObservationReferenceRangeTemplate): string {
    const codeDisplay = template.display || template.code?.display || template.code?.code || 'reference-range';
    const type = template.range?.type?.text || template.range?.type?.coding?.[0]?.display;
    return [codeDisplay, type].filter(Boolean).join(' - ');
  }

  private buildRangeExtension(range?: ObservationReferenceRange): Extension | null {
    if (!range) {
      return null;
    }
    const inner: Extension[] = [];
    if (range.type) {
      inner.push({ url: 'type', valueCodeableConcept: range.type });
    }
    if (range.low) {
      inner.push({ url: 'low', valueQuantity: range.low });
    }
    if (range.high) {
      inner.push({ url: 'high', valueQuantity: range.high });
    }
    if (range.appliesTo?.length) {
      range.appliesTo.forEach(apply => inner.push({ url: 'appliesTo', valueCodeableConcept: apply }));
    }
    if (range.age) {
      inner.push({ url: 'age', valueRange: range.age });
    }
    if (range.text) {
      inner.push({ url: 'text', valueString: range.text });
    }
    if (!inner.length) {
      return null;
    }
    return { url: this.extensionUrl, extension: inner };
  }

  private parseRangeExtension(extension?: Extension): ObservationReferenceRange {
    const range: ObservationReferenceRange = {};
    if (!extension?.extension?.length) {
      return range;
    }
    for (const inner of extension.extension) {
      switch (inner.url) {
        case 'type':
          range.type = inner.valueCodeableConcept;
          break;
        case 'low':
          range.low = inner.valueQuantity;
          break;
        case 'high':
          range.high = inner.valueQuantity;
          break;
        case 'appliesTo':
          range.appliesTo = [...(range.appliesTo ?? []), inner.valueCodeableConcept!];
          break;
        case 'age':
          range.age = inner.valueRange;
          break;
        case 'text':
          range.text = inner.valueString;
          break;
      }
    }
    return range;
  }

  private buildUseContext(range?: ObservationReferenceRange) {
    if (!range) {
      return undefined;
    }
    const contexts: any[] = [];
    const genderCode = { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'gender' };
    if (range.appliesTo?.length) {
      range.appliesTo.forEach(apply => {
        contexts.push({ code: genderCode, valueCodeableConcept: apply });
      });
    }
    if (range.age?.low || range.age?.high) {
      contexts.push({
        code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'age' },
        valueRange: range.age
      });
    }
    return contexts.length ? contexts : undefined;
  }

  private matchesPatientContext(range: ObservationReferenceRange, patient?: Patient): boolean {
    if (!patient) {
      return true;
    }
    const gender = (patient.gender ?? '').toLowerCase();
    console.log(range, gender);
// $#$    /* debugger; */
    if (range.appliesTo?.length) {
      const matchesGender = range.appliesTo.some(apply => this.matchesGender(apply, gender));
      if (!matchesGender) {
        return false;
      }
    }
    if (range.age?.low || range.age?.high) {
      const ageYears = this.getPatientAgeInYears(patient);
      if (ageYears == null) {
        return true;
      }
      console.log(range, ageYears);
      const within = this.isAgeWithinRange(ageYears, range.age);
      if (!within) {
        return false;
      }
    }
    return true;
  }

  private matchesGender(apply: CodeableConcept | undefined, gender: string): boolean {
    if (!apply || !gender) {
      return true;
    }
    // Ensure we have the administrative-gender codes loaded (lazy)
    this.loadAdminGenderCodesIfNeeded();

    const codes = apply.coding ?? [];
    const target = (gender || '').toString().toLowerCase();

    // Prefer code matching only (case-insensitive). If the patient.gender value is
    // not present in the known administrative-gender codes, emit a debug warning
    // so callers know the assumption about patient.gender values may be violated.
    if (this.adminGenderCodes.length && !this.adminGenderCodes.map(c => c.toLowerCase()).includes(target)) {
      console.debug('[ObservationReferenceRangeService] patient.gender value not found in administrative-gender ValueSet codes:', gender);
    }

    return codes.some(c => ((c.code ?? '') as string).toLowerCase() === target);
  }

  private loadAdminGenderCodesIfNeeded(): void {
    if (this.adminGenderCodes.length) return;
    try {
      const url = this.formFieldsDataService.allUrls?.patient?.gender;
      if (!url) return;
      this.http.get(url, { context: new HttpContext().set(LoadingUIEnabled, false) }).subscribe({
        next: (val: any) => {
          try {
            const codes = this.formFieldsDataService.baseFunctionToRetrieveValueCodes(val);
            if (Array.isArray(codes) && codes.length) {
              this.adminGenderCodes = codes.map(c => (c || '').toString());
            }
          } catch (e) {
            // non-blocking
          }
        },
        error: () => { /* non-blocking */ }
      });
    } catch (e) {
      // non-blocking
    }
  }

  private getPatientAgeInYears(patient: Patient): number | null {
    if (!patient?.birthDate) {
      return null;
    }
    const birth = new Date(patient.birthDate);
    if (Number.isNaN(birth.getTime())) {
      return null;
    }
    const diffMs = Date.now() - birth.getTime();
    return diffMs / (1000 * 60 * 60 * 24 * 365.25);
  }

  private isAgeWithinRange(ageYears: number, range?: Range): boolean {
    if (!range) {
      return true;
    }
    if (range.low?.value != null) {
      const minYears = this.convertToYears(range.low);
      if (minYears != null && ageYears < minYears) {
        return false;
      }
    }
    if (range.high?.value != null) {
      const maxYears = this.convertToYears(range.high);
      if (maxYears != null && ageYears > maxYears) {
        return false;
      }
    }
    return true;
  }

  private convertToYears(quantity: Quantity): number | null {
    if (!quantity?.value) {
      return null;
    }
    const unit = (quantity.code || quantity.unit || '').toLowerCase();
    const value = Number(quantity.value);
    if (Number.isNaN(value)) {
      return null;
    }
    switch (unit) {
      case 'a':
      case 'yr':
      case 'year':
      case 'years':
        return value;
      case 'mo':
      case 'month':
      case 'months':
        return value / 12;
      case 'wk':
      case 'week':
      case 'weeks':
        return value / 52;
      case 'd':
      case 'day':
      case 'days':
        return value / 365;
      default:
        return value;
    }
  }

  describeRange(range?: ObservationReferenceRange): string {
    if (!range) {
      return '-';
    }
    const parts: string[] = [];
    if (range.low?.value != null) {
      const lowUnit = range.low.unit || range.low.code || '';
      parts.push(`Low: ${range.low.value} ${lowUnit}`.trim());
    }
    if (range.high?.value != null) {
      const highUnit = range.high.unit || range.high.code || '';
      parts.push(`High: ${range.high.value} ${highUnit}`.trim());
    }
    if (!parts.length && range.text) {
      parts.push(range.text);
    }
    return parts.length ? parts.join(' | ') : 'Configured';
  }

  describeAppliesTo(range?: ObservationReferenceRange): string {
    if (!range?.appliesTo?.length) {
      return 'All patients';
    }
    return range.appliesTo
      .map(concept => concept?.text || concept?.coding?.[0]?.display || concept?.coding?.[0]?.code || 'Unknown')
      .join(', ');
  }

  describeAge(range?: ObservationReferenceRange): string {
    const age = range?.age;
    if (!age || (!age.low && !age.high)) {
      return 'All ages';
    }
    const segments: string[] = [];
    if (age.low?.value != null) {
      const unit = age.low.unit || age.low.code || '';
      segments.push(`>= ${age.low.value} ${unit}`.trim());
    }
    if (age.high?.value != null) {
      const unit = age.high.unit || age.high.code || '';
      segments.push(`<= ${age.high.value} ${unit}`.trim());
    }
    return segments.join(' and ');
  }

  private classifyValue(observation: Observation, range: ObservationReferenceRange): 'low' | 'high' | 'within' | 'unknown' {
    const valueQuantity = observation?.valueQuantity;
    if (!valueQuantity?.value) {
      return 'unknown';
    }
    const value = Number(valueQuantity.value);
    if (Number.isNaN(value)) {
      return 'unknown';
    }
    if (range.low?.value != null) {
      const low = Number(range.low.value);
      if (!Number.isNaN(low) && value < low) {
        return 'low';
      }
    }
    if (range.high?.value != null) {
      const high = Number(range.high.value);
      if (!Number.isNaN(high) && value > high) {
        return 'high';
      }
    }
    if (range.low?.value != null || range.high?.value != null) {
      return 'within';
    }
    return 'unknown';
  }
}
